export const SHARED_ACCESS_STORAGE_KEY = 'tenantscore_score_access_v2_shared';
export const LEGACY_ACCESS_STORAGE_KEYS = ['tenantscore_score_access_v1_landlord', 'tenantscore_score_access_v1_agent'];

export const accessStorageKey = (variant) => `tenantscore_score_access_v1_${variant}`;

export const parseAccessMap = (raw) => {
  try {
    const parsed = raw ? JSON.parse(raw) : {};
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

export const readAccessMap = (variant) => {
  // Keep landlord + agent tenant score views in sync by reading from one shared map.
  const merged = {
    ...parseAccessMap(localStorage.getItem(SHARED_ACCESS_STORAGE_KEY)),
    ...parseAccessMap(localStorage.getItem(accessStorageKey(variant))),
  };
  LEGACY_ACCESS_STORAGE_KEYS.forEach((key) => {
    Object.assign(merged, parseAccessMap(localStorage.getItem(key)));
  });
  return merged;
};

export const persistAccessMap = (map, variant) => {
  try {
    const payload = JSON.stringify(map);
    localStorage.setItem(SHARED_ACCESS_STORAGE_KEY, payload);
    localStorage.setItem(accessStorageKey(variant), payload);
    LEGACY_ACCESS_STORAGE_KEYS.forEach((key) => localStorage.setItem(key, payload));
  } catch {
    /* ignore quota */
  }
};

export const writeAccessStatus = (variant, tenantUserId, status) => {
  if (!tenantUserId) return;
  const map = { ...readAccessMap(variant), [tenantUserId]: status };
  persistAccessMap(map, variant);
};

export const getAccessStatusStr = (variant, tenantUserId) => {
  const s = readAccessMap(variant)[tenantUserId];
  if (s === 'granted') return 'granted';
  if (s === 'requested') return 'requested';
  if (s === 'rejected') return 'rejected';
  return 'none';
};

/** Heuristic: backend message indicates the tenant denied score access (not merely “still pending”). */
export const messageLooksLikeAccessRejected = (message) => {
  const lower = String(message || '').toLowerCase();
  if (!lower.trim()) return false;
  if (/\breject(ed|ion)?\b/.test(lower) || lower.includes('declined')) return true;
  if (lower.includes('denied') && (lower.includes('access') || lower.includes('request') || lower.includes('score'))) return true;
  return false;
};

export const tenantUserIdFromOutgoingScoreRequest = (req) => {
  return String(
    req?.tenantUserId ||
      req?.tenantId ||
      req?.subjectUserId ||
      req?.targetTenantUserId ||
      req?.targetUserId ||
      ''
  ).trim();
};

/** Apply server outgoing score-access rows to localStorage (REJECTED / PENDING / APPROVED). Returns true if storage changed. */
export const mergeOutgoingScoreRequestsIntoAccessMap = (variant, list) => {
  const arr = Array.isArray(list) ? list : [];
  if (arr.length === 0) return false;
  const sorted = [...arr].sort(
    (a, b) =>
      new Date(a?.createdAt || a?.updatedAt || 0).getTime() -
      new Date(b?.createdAt || b?.updatedAt || 0).getTime()
  );
  const map = { ...readAccessMap(variant) };
  let changed = false;
  for (const req of sorted) {
    const tenantId = tenantUserIdFromOutgoingScoreRequest(req);
    if (!tenantId) continue;
    const st = String(req?.status ?? '').toUpperCase();
    if (map[tenantId] === 'granted') continue;
    let next = null;
    if (st === 'REJECTED' || st === 'DENIED' || st === 'DECLINED') {
      next = 'rejected';
    } else if (st === 'APPROVED' || st === 'GRANTED' || st === 'ACCEPTED') {
      next = 'granted';
    } else if (
      st === 'PENDING' ||
      st === 'AWAITING' ||
      st === 'AWAITING_APPROVAL' ||
      st === 'OPEN' ||
      st === 'SUBMITTED'
    ) {
      next = 'requested';
    }
    if (next !== null && map[tenantId] !== next) {
      map[tenantId] = next;
      changed = true;
    }
  }
  if (changed) {
    persistAccessMap(map, variant);
  }
  return changed;
};

const nestedUserId = (record, ...path) => {
  let current = record;
  for (const key of path) {
    if (!current || typeof current !== 'object') return null;
    current = current[key];
  }
  return current;
};

/** Skip IDs that belong to the request/booking row itself, not a user account. */
const isRecordEntityId = (record, id) => {
  const candidate = String(id ?? '').trim().toLowerCase();
  if (!candidate) return true;
  const entityIds = [
    record?.id,
    record?.requestId,
    record?.requestID,
    record?.bookingId,
    record?.bookingID,
    record?.unitId,
    record?.unitID,
    record?.propertyId,
    record?.propertyID,
    record?.propertyUnitId,
    record?.propertyUnitID,
  ]
    .map((value) => String(value ?? '').trim().toLowerCase())
    .filter(Boolean);
  return entityIds.includes(candidate);
};

const pickFirstUserIdFromRecord = (record, ...candidates) => {
  for (const id of candidates) {
    const value = String(id ?? '').trim();
    if (!value || isRecordEntityId(record, value)) continue;
    return value;
  }
  return null;
};

/** Unified logic for extracting the tenant/guest user UUID from a booking or unit-request record. */
export const getGuestUserIdFromBooking = (record) => {
  if (!record) return null;
  return pickFirstUserIdFromRecord(
    record,
    record.guestUserId,
    record.guestUserID,
    record.requesterUserId,
    record.requesterUserID,
    record.tenantUserId,
    record.tenantUserID,
    nestedUserId(record, 'guest', 'userId'),
    nestedUserId(record, 'guest', 'userID'),
    nestedUserId(record, 'requester', 'userId'),
    nestedUserId(record, 'requester', 'userID'),
    nestedUserId(record, 'requesterUser', 'userId'),
    nestedUserId(record, 'requesterUser', 'userID'),
    nestedUserId(record, 'tenant', 'userId'),
    nestedUserId(record, 'tenant', 'userID'),
    nestedUserId(record, 'tenantUser', 'userId'),
    nestedUserId(record, 'tenantUser', 'userID'),
    nestedUserId(record, 'user', 'userId'),
    nestedUserId(record, 'user', 'userID'),
    nestedUserId(record, 'user', 'id'),
    record.assignedUserId,
    record.assignedUserID,
    record.userId,
    record.userID
  );
};

/** Alias used by landlord unit-request flows. */
export const getTenantUserIdFromUnitRequest = getGuestUserIdFromBooking;

/** Display name for the tenant/guest on a booking or unit-request card. */
export const getGuestDisplayNameFromBooking = (record) => {
  if (!record) return 'Guest';
  const guest = record.guest || record.tenant || record.requester || record.requesterUser || null;
  const fromGuestObject = [guest?.firstName, guest?.lastName].filter(Boolean).join(' ').trim();
  return (
    record.guestName ||
    fromGuestObject ||
    [record.requesterFirstName, record.requesterLastName].filter(Boolean).join(' ').trim() ||
    [record.userFirstName, record.userLastName].filter(Boolean).join(' ').trim() ||
    record.tenantName ||
    'Guest'
  );
};
